import { useChatManager } from "@/hooks/use-chat-manager";
import { useLimitedInput } from "@/hooks/use-limited-input";
import { CHAT_MODE, useChatState } from "@/store/chat-state";
import { useDrawerState } from "@/store/drawer-state";
import { WIDGET_ORIGIN } from "@/constants/widget-origin";
import type { BootstrapHandle } from "@/utils/bootstrap-web-component";
import { bootstrapWidgetWebComponent } from "@/utils/bootstrap-web-component";
import styled from "@emotion/styled";
import { useMediaQuery } from "@uidotdev/usehooks";
import { XIcon } from "lucide-react";
import type { FC } from "react";
import { useEffect, useRef } from "react";
import {
  breakpoints,
  colors,
  radius,
  shadows,
  spacing,
} from "../design-tokens";
import { ChatContent } from "../drawer-components/chat/chat-content";
import { ActionButton } from "../primitives/action-button";
import { DisclaimerFooter } from "../primitives/disclaimer-footer";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
} from "../primitives/drawer-primitive";
import { QueryInput } from "../primitives/query-input";
import vaulDrawerCss from "../primitives/vaul-drawer.css?inline";
import { DRAWER_PLACEHOLDER } from "@/constants/search-constants";
import { resolvePlaceholder } from "@/utils/resolve-widget-content";

const StyledDrawerContent = styled.div`
  background: ${colors.surface};
  display: flex;
  flex-direction: column;
  height: 80dvh;
  max-width: 800px;
  width: 45%;
  min-width: 520px;
  outline: none;
  ${shadows.main}
  overflow: hidden;
  border-left: 1px solid ${colors.border};
  border-top-left-radius: ${radius.xl}px;
  border-bottom-left-radius: ${radius.xl}px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  @media (max-width: ${breakpoints.md - 1}px) {
    min-width: 100%;
    width: 100%;
    max-height: 100dvh;
    height: 90dvh;
    border-top-left-radius: ${radius.xl}px;
    border-top-right-radius: ${radius.xl}px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    border-top: 1px solid ${colors.border};
  }
`;

const StyledDrawerHeader = styled(DrawerHeader)`
  padding: ${spacing.xl}px;
  display: flex;
  gap: ${spacing.md}px;
  align-items: flex-start;
  justify-content: space-between;
  flex-shrink: 0;
  border-bottom: 1px solid ${colors.border};
  background: ${colors.surface};

  @media (max-width: ${breakpoints.md - 1}px) {
    padding: ${spacing.lg}px;
  }
`;

const InputSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md}px;
  align-items: flex-start;
  flex: 1;
`;

const InputContainer = styled.div`
  width: 100%;
`;

const ContentArea = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

type DrawerComponentProps = {
  placeholder?: string;
};

export const DrawerComponent: FC<DrawerComponentProps> = ({
  placeholder = DRAWER_PLACEHOLDER,
}) => {
  const { isDrawerOpen, closeDrawer } = useDrawerState();
  const wasDrawerOpenRef = useRef(false);
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.md - 1}px)`);
  const { submitQuery, contextUrl } = useChatState();
  const { userQuery, status, response, mode, error }
    = useChatManager();

  // Use the actual query from state as the initial value to persist the last query
  const {
    value,
    setValue,
    isValid,
    error: limitedInputError,
  } = useLimitedInput("", {
    showError: true,
  });

  // Seed the editable input only when the drawer opens. Context-triggered
  // summaries intentionally keep it blank, even after contextUrl is cleared.
  useEffect(() => {
    const wasDrawerOpen = wasDrawerOpenRef.current;
    wasDrawerOpenRef.current = isDrawerOpen;

    if (!isDrawerOpen || wasDrawerOpen) {
      return;
    }

    if (!contextUrl && userQuery) {
      setValue(userQuery);
    }
    else {
      setValue("");
    }
  }, [contextUrl, isDrawerOpen, setValue, userQuery]);

  const handleInputSubmit = (query: string) => {
    if (!isValid) {
      return;
    }
    submitQuery(query, WIDGET_ORIGIN.DRAWER);
    setValue(query);
  };

  return (
    <div id="container">
      <Drawer
        open={isDrawerOpen}
        onOpenChange={(open: boolean) => {
          if (!open) {
            closeDrawer();
          }
        }}
        noBodyStyles
        modal={false}
        direction={isMobile ? "bottom" : "right"}
        handleOnly={!isMobile}
      >
        <DrawerContent container={null} asChild>
          <StyledDrawerContent
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <StyledDrawerHeader>
              <VisuallyHidden>
                <DrawerDescription>
                  AI assistant to help answer your questions
                </DrawerDescription>
              </VisuallyHidden>

              <InputSection>
                <InputContainer>
                  <QueryInput
                    placeholder={placeholder}
                    autoFocus={mode === CHAT_MODE.INPUT}
                    value={value}
                    onChange={setValue}
                    onSubmit={handleInputSubmit}
                    error={limitedInputError}
                  />
                </InputContainer>
                <DisclaimerFooter layout="drawer" />
              </InputSection>

              <DrawerClose asChild>
                <ActionButton
                  variant="ghost"
                  onClick={closeDrawer}
                  size="small"
                >
                  Close
                  <XIcon size={16} strokeWidth={3} />
                </ActionButton>
              </DrawerClose>
            </StyledDrawerHeader>

            <ContentArea>
              <ChatContent
                userQuery={userQuery}
                status={status}
                response={response}
                mode={mode}
                error={error}
              />
            </ContentArea>
          </StyledDrawerContent>
        </DrawerContent>
      </Drawer>
    </div>
  );
};

class DrawerWebComponent extends HTMLElement {
  private handle: BootstrapHandle | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback() {
    const placeholder = this.getAttribute("placeholder") ?? undefined;
    // Inject vaul drawer animation CSS into the shadow root before mounting.
    const styleId = "vaul-drawer-shadow-style";
    if (!this.shadowRoot?.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = vaulDrawerCss;
      this.shadowRoot?.appendChild(style);
    }

    this.handle = bootstrapWidgetWebComponent(this, {
      componentName: "drawer",
      render: config => (
        <DrawerComponent
          placeholder={resolvePlaceholder(
            placeholder,
            config.placeholderText,
            DRAWER_PLACEHOLDER,
          )}
        />
      ),
    });
  }

  disconnectedCallback() {
    this.handle?.unmount();
    this.handle = null;
  }
}

export default DrawerWebComponent;
